/**
 * SaaMatrixView — read-only allocation table for a chosen SAA (#11 Inc 7, on-screen).
 *
 * Given an allocation code (the agreement/questionnaire's chosen SAA), shows that
 * profile's asset-class allocation matrix (Min / Neutral / Max %, with benchmarks)
 * from the Admin-managed SAA tables. Until Inc 6 the agreement still stores the
 * legacy enum, so CASH/CASH_PLUS are mapped to the seeded profile codes MM/MM_PLUS
 * (identity for SAA00..SAA100). After Inc 6 the map becomes a no-op.
 */
import { Loader, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { saaApi } from '../api/saa';

const ENUM_TO_CODE: Record<string, string> = { CASH: 'MM', CASH_PLUS: 'MM_PLUS' };

function pct(v: number | null): string {
  return v == null ? '—' : `${v}%`;
}

export function SaaMatrixView({ allocationCode }: { allocationCode: string | null | undefined }) {
  const code = allocationCode ? (ENUM_TO_CODE[allocationCode] ?? allocationCode) : null;
  const q = useQuery({
    queryKey: ['saa', 'alloc', code],
    queryFn: () => saaApi.allocations(code as string),
    enabled: !!code,
  });

  if (!code) return null;
  if (q.isLoading) return <Loader size="xs" />;
  if (q.isError || !q.data) return null;

  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>Strategic allocation — {q.data.label}</Text>
      <Table withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Asset class</Table.Th>
            <Table.Th>Benchmark</Table.Th>
            <Table.Th w={70}>Min</Table.Th>
            <Table.Th w={80}>Neutral</Table.Th>
            <Table.Th w={70}>Max</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {q.data.cells.map((c) => (
            <Table.Tr key={c.asset_code}>
              <Table.Td><Text size="sm" pl={c.parent_code ? 'md' : 0} fw={c.parent_code ? 400 : 600}>{c.asset_label}</Text></Table.Td>
              <Table.Td><Text size="xs" c="dimmed">{c.benchmark_ticker || '—'}</Text></Table.Td>
              <Table.Td><Text size="sm">{pct(c.min_pct)}</Text></Table.Td>
              <Table.Td><Text size="sm">{pct(c.neutral_pct)}</Text></Table.Td>
              <Table.Td><Text size="sm">{pct(c.max_pct)}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
